import * as React from 'react';
import type { MultiCellSelection } from '../lib/availability-types';
import {
  buildMultiSelectAnchorTestId,
  resolveMultiSelectAnchorElement,
} from '../lib/multi-select-anchor';

export interface UseMultiSelectAnchorResult {
  multiSelection: MultiCellSelection | null;
  /** Anchor element ref — pass directly to `UnifiedSelectPopover`'s `anchorRef` prop. */
  anchorRef: React.RefObject<HTMLElement | null>;
  /**
   * Bumped every time `updateSelection` (re-)resolves the anchor DOM element, including a
   * refresh call with the SAME selection reference — e.g. `DetailAvailabilityCard` re-fires
   * `onMultiSelectionChange(multiSelection)` after its calendar section collapses and
   * reopens, remounting the anchor cell's DOM node (CU-86cb8grgk). Pass to
   * `UnifiedSelectPopover`'s `anchorVersion` prop so its position effect recomputes even
   * though `selection.anchor.unitId`/`periodId` are unchanged.
   */
  anchorVersion: number;
  /**
   * Replaces the selection AND synchronously resolves+stores the anchor DOM element
   * for the new selection's `anchor` cell — the anchor-consistent setter. Call this
   * from click/drag handlers (or an `onMultiSelectionChange` callback, which itself
   * fires synchronously from `AvailabilityGrid`'s own click/drag handler), never from
   * a `useEffect` reacting to `multiSelection`. See `resolveMultiSelectAnchorElement`'s
   * doc comment for why: a delayed effect leaves `UnifiedSelectPopover`'s own
   * positioning `useLayoutEffect` reading a stale or null anchor on first open.
   */
  updateSelection: (selection: MultiCellSelection | null) => void;
  /**
   * Raw functional-updater setter for selection changes that narrow the EXISTING
   * selection without moving its anchor cell (e.g. dropping conflicted/skipped cells
   * after a batch-reserve/release response) — the anchor cell's DOM node hasn't
   * disappeared, so the current `anchorRef.current` stays valid and does not need
   * re-resolving.
   */
  narrowSelection: (
    updater: (current: MultiCellSelection | null) => MultiCellSelection | null,
  ) => void;
}

/**
 * Owns `multiSelection` state plus the anchor DOM ref `UnifiedSelectPopover` positions
 * itself against, keeping the two updated in the same synchronous tick. Shared across
 * the three surfaces that render `UnifiedSelectPopover` (inventory catalog, media unit
 * detail, offer-draft availability tab) — each passes its own grid's `data-testid`
 * prefix (e.g. `"rich-availability-cell"` or `"cell"`).
 */
export function useMultiSelectAnchor(testIdPrefix: string): UseMultiSelectAnchorResult {
  const [multiSelection, setMultiSelectionState] = React.useState<MultiCellSelection | null>(null);
  const [anchorVersion, setAnchorVersion] = React.useState(0);
  const anchorRef = React.useRef<HTMLElement | null>(null);

  const updateSelection = React.useCallback(
    (selection: MultiCellSelection | null) => {
      anchorRef.current = selection
        ? resolveMultiSelectAnchorElement(
            buildMultiSelectAnchorTestId(
              testIdPrefix,
              selection.anchor.unitId,
              selection.anchor.periodId,
            ),
          )
        : null;
      setAnchorVersion((v) => v + 1);
      setMultiSelectionState(selection);
    },
    [testIdPrefix],
  );

  const narrowSelection = React.useCallback(
    (updater: (current: MultiCellSelection | null) => MultiCellSelection | null) => {
      setMultiSelectionState(updater);
    },
    [],
  );

  return { multiSelection, anchorRef, anchorVersion, updateSelection, narrowSelection };
}
